import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { db } from "../../../../config/firebase";
import { doc, collection, getDoc, setDoc } from "firebase/firestore";
import { useStepFirestore } from "../../../../hooks/useStepFirestore";
import { useSubscription } from "../../../../hooks/useSubscription";
import { StepCoachModal } from "../../../../app/pages/dashboards/steps/StepCoachModal";
import {
  StepConfig,
  FormFieldConfig,
  RadioOption,
  SelectOption,
  buildValidationSchema,
  getInitialValues,
} from "./stepConfig";
import {
  STEP_CONFIGS,
  StepDifficulty,
} from "../../../../app/pages/dashboards/steps/stepConfig";

// Human-readable relative timestamps (mirrors timeAgo in ProjectDashboardPage)
const timeAgo = (isoString: string): string => {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "yesterday";
  if (diffD < 30) return `${diffD}d ago`;
  return `${Math.floor(diffD / 30)}mo ago`;
};

// DESIGN.md category colours — mirrors StepsList.tsx categoryConfig
const getCategoryForStep = (stepNumber: number): { label: string; color: string; lightColor: string } => {
  if (stepNumber <= 5)  return { label: "Market Opportunity",      color: "#3B82F6", lightColor: "#DBEAFE" };
  if (stepNumber <= 10) return { label: "Customer Understanding",  color: "#8B5CF6", lightColor: "#EDE9FE" };
  if (stepNumber <= 14) return { label: "Value & Product",         color: "#EC4899", lightColor: "#FCE7F3" };
  if (stepNumber <= 19) return { label: "Business Model",          color: "#F59E0B", lightColor: "#FEF3C7" };
  return                       { label: "Validation & Execution",  color: "#10B981", lightColor: "#D1FAE5" };
};

// Step difficulty lookup — sourced from main stepConfig.ts
const stepDifficultyMap: Record<number, StepDifficulty> = STEP_CONFIGS.reduce(
  (acc, s) => ({ ...acc, [s.stepNumber]: s.difficulty }),
  {} as Record<number, StepDifficulty>
);

// Step time lookup — sourced from main stepConfig.ts
const stepTimeMap: Record<number, string> = STEP_CONFIGS.reduce(
  (acc, s) => ({ ...acc, [s.stepNumber]: s.estimatedTime }),
  {} as Record<number, string>
);

const difficultyStyle: Record<StepDifficulty, { bg: string; color: string; label: string }> = {
  easy:   { bg: "#D1FAE5", color: "#10B981", label: "Easy" },
  medium: { bg: "#FEF3C7", color: "#F59E0B", label: "Medium" },
  hard:   { bg: "#FEE2E2", color: "#EF4444", label: "Hard" },
};

interface GenericStepFormProps {
  className?: string;
  innerPadding?: string;
  config: StepConfig;
}

/**
 * Radio button group component
 */
const RadioGroup: React.FC<{
  field: FormFieldConfig;
  value: string;
  onChange: (value: string) => void;
}> = memo(({ field, value, onChange }) => {
  return (
    <div className="row" data-kt-control="toggle-buttons">
      <fieldset id={field.name}>
        {field.options?.map((option: RadioOption, index: number) => (
          <React.Fragment key={`${field.name}_option${index}`}>
            <input
              id={`${field.name}_option${index}`}
              type="radio"
              className="btn-check"
              name={field.name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
            />
            <label
              className={`col btn btn-lg btn-outline btn-bg-light btn-color-gray-600 btn-active-light-primary border py-5 px-4 m-2 min-w-125px ${
                value === option.value ? "active border-primary" : "border-gray-300"
              }`}
              htmlFor={`${field.name}_option${index}`}
            >
              <span className="text-gray-800 fw-bold fs-7">{option.label}</span>
            </label>
          </React.Fragment>
        ))}
      </fieldset>
    </div>
  );
});

RadioGroup.displayName = "RadioGroup";

/**
 * Text area component with optional example tooltip
 */
const TextAreaField: React.FC<{
  field: FormFieldConfig;
  value: string;
  onChange: (value: string) => void;
}> = memo(({ field, value, onChange }) => {
  return (
    <>
      {field.helpText && (
        <div className="row" data-kt-control="toggle-buttons">
          <span className="fw-bold fs-7 text-muted">{field.helpText}</span>
          {field.exampleText && (
            <span
              className="btn btn-custom h-40px w-40px h-lg-50px w-lg-50px btn-icon fs-7"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-kt-menu-dismiss="true"
              title={field.exampleText}
            >
              <i className="bi bi-question-circle text-success fs-4"></i>
              Example
            </span>
          )}
        </div>
      )}
      <div className="row mt-5" data-kt-control="toggle-buttons">
        <fieldset id={field.name}>
          <textarea
            className="form-control fs-7 fw-normal"
            id={field.name}
            name={field.name}
            placeholder={field.placeholder}
            rows={field.rows || 5}
            onChange={(e) => onChange(e.target.value)}
            value={value}
          />
        </fieldset>
      </div>
    </>
  );
});

TextAreaField.displayName = "TextAreaField";

/**
 * Select dropdown component
 */
const SelectField: React.FC<{
  field: FormFieldConfig;
  value: string;
  onChange: (value: string) => void;
}> = memo(({ field, value, onChange }) => {
  return (
    <div className="row mt-5" data-kt-control="toggle-buttons">
      <fieldset id={field.name}>
        {field.label && (
          <label className="fs-6 form-label fw-bolder text-dark">
            {field.label}
          </label>
        )}
        <select
          className="form-select form-select-lg form-select-solid"
          id={field.name}
          name={field.name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.selectOptions?.map((option: SelectOption, index: number) => (
            <option key={`${field.name}_select_${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </fieldset>
    </div>
  );
});

SelectField.displayName = "SelectField";

/**
 * Generic Step Form Component
 * Replaces 24 near-identical step form components with a single reusable component
 */
const GenericStepForm: React.FC<GenericStepFormProps> = memo(
  ({ className = "", innerPadding = "", config }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as Record<string, string> | null;

    const {
      formData,
      loading: firestoreLoading,
      fetchFormData,
      writeFormData,
      updateProgress,
      userID,
      projectID,
    } = useStepFirestore<Record<string, string>>(config.stepNumber);

    const { canUseFeature } = useSubscription();
    const [showCoach, setShowCoach] = useState(false);
    const category = getCategoryForStep(config.stepNumber);

    const [fieldValues, setFieldValues] = useState<Record<string, string>>(
      getInitialValues(config)
    );
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasChangesRef = useRef(false);

    // Notes state
    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [notesSaveStatus, setNotesSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const notesAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const notesHasChanges = useRef(false);

    // Step timestamps
    const [stepStartedAt, setStepStartedAt] = useState<string | null>(null);
    const [stepUpdatedAt, setStepUpdatedAt] = useState<string | null>(null);

    // Notes Firestore path helper (mirrors useStepFirestore path)
    const getStepDocRef = useCallback(() => {
      if (!userID || !projectID) return null;
      const stepKey = `step${config.stepNumber}`;
      return doc(collection(doc(collection(doc(db, "users", userID), "projects"), projectID), "progress"), stepKey);
    }, [userID, projectID, config.stepNumber]);

    // Fetch notes + timestamps from Firestore on mount (single read)
    useEffect(() => {
      if (!userID || !projectID) return;
      const stepRef = getStepDocRef();
      if (!stepRef) return;
      getDoc(stepRef).then((snap) => {
        const data = snap.data();
        if (data?.notes) setNotes(data.notes as string);
        if (data?.startedAt) setStepStartedAt(data.startedAt as string);
        if (data?.updatedAt) setStepUpdatedAt(data.updatedAt as string);
      }).catch(() => {/* non-critical */});
    }, [userID, projectID, getStepDocRef]);

    // Persist notes to Firestore
    const saveNotes = useCallback(async (value: string) => {
      const stepRef = getStepDocRef();
      if (!stepRef) return;
      setNotesSaveStatus("saving");
      try {
        await setDoc(stepRef, { notes: value }, { merge: true });
        notesHasChanges.current = false;
        setNotesSaveStatus("saved");
        setTimeout(() => setNotesSaveStatus((s) => s === "saved" ? "idle" : s), 2500);
      } catch {
        setNotesSaveStatus("error");
        setTimeout(() => setNotesSaveStatus((s) => s === "error" ? "idle" : s), 4000);
      }
    }, [getStepDocRef]);

    // Notes auto-save debounce (3 seconds)
    const handleNotesChange = useCallback((value: string) => {
      setNotes(value);
      notesHasChanges.current = true;
      if (notesAutoSaveRef.current) clearTimeout(notesAutoSaveRef.current);
      notesAutoSaveRef.current = setTimeout(() => {
        if (notesHasChanges.current) saveNotes(value);
      }, 3000);
    }, [saveNotes]);

    // Cleanup notes timer on unmount
    useEffect(() => {
      return () => {
        if (notesAutoSaveRef.current) clearTimeout(notesAutoSaveRef.current);
      };
    }, []);

    // Populate form with fetched data
    useEffect(() => {
      if (formData) {
        setFieldValues((prev) => ({ ...prev, ...formData }));
      }
    }, [formData]);

    // Fetch form data on mount
    useEffect(() => {
      if (userID && projectID) {
        fetchFormData();
      }
    }, [userID, projectID, fetchFormData]);

    // Calculate progress based on filled fields
    const calculateProgress = useCallback(() => {
      const fields = config.sections.flatMap((s) => s.fields);
      const filledFields = fields.filter((f) => {
        const value = fieldValues[f.name];
        return value !== undefined && String(value).trim() !== "";
      });
      const progressPerField = 100 / fields.length;
      return Math.round(filledFields.length * progressPerField);
    }, [config.sections, fieldValues]);

    // Inline progress stats
    const totalFields = config.sections.flatMap((s) => s.fields).length;
    const filledCount = config.sections.flatMap((s) => s.fields).filter((f) => {
      const v = fieldValues[f.name];
      return v !== undefined && String(v).trim() !== "";
    }).length;

    // Auto-save logic
    const performAutoSave = useCallback(async () => {
      if (!hasChangesRef.current || !userID || !projectID) return;
      setSaveStatus("saving");
      try {
        await writeFormData(fieldValues);
        await updateProgress(calculateProgress());
        hasChangesRef.current = false;
        setStepUpdatedAt(new Date().toISOString());
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus((s) => s === "saved" ? "idle" : s), 2500);
      } catch {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus((s) => s === "error" ? "idle" : s), 4000);
      }
    }, [fieldValues, userID, projectID, writeFormData, updateProgress, calculateProgress]);

    // Debounced auto-save on field change
    useEffect(() => {
      if (!hasChangesRef.current) return;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        performAutoSave();
      }, 3000);
      return () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      };
    }, [fieldValues, performAutoSave]);

    // Cleanup on unmount — save if pending
    useEffect(() => {
      return () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      };
    }, []);

    // Handle field value changes
    const handleFieldChange = useCallback((fieldName: string, value: string) => {
      hasChangesRef.current = true;
      setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    }, []);

    // Formik configuration
    const formik = useFormik({
      initialValues: getInitialValues(config),
      validationSchema: buildValidationSchema(config),
      enableReinitialize: true,
      onSubmit: async (_, { setSubmitting }) => {
        setLoading(true);
        setSaveStatus("saving");
        try {
          if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
          await writeFormData(fieldValues);
          await updateProgress(calculateProgress());
          hasChangesRef.current = false;
          setStepUpdatedAt(new Date().toISOString());
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus((s) => s === "saved" ? "idle" : s), 2500);
        } catch {
          setSaveStatus("error");
          setTimeout(() => setSaveStatus((s) => s === "error" ? "idle" : s), 4000);
        } finally {
          setLoading(false);
          setSubmitting(false);
        }
      },
    });

    // Keyboard shortcuts: Ctrl/Cmd+S to save, Escape to go back, Alt+←/→ to navigate steps
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Ctrl/Cmd+S → save form (works even when focused in a text field)
        if ((e.metaKey || e.ctrlKey) && e.key === "s") {
          e.preventDefault();
          if (!formik.isSubmitting && !loading) {
            formik.handleSubmit();
          }
          return;
        }

        const target = e.target as HTMLElement;
        const isEditable = ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);

        // Escape → back to project (not in editable element, not in modal)
        if (e.key === "Escape" && !isEditable && !showCoach && locationState) {
          navigate("/project", { state: locationState });
          return;
        }

        // Alt+ArrowLeft → previous step (skip when typing)
        if (e.altKey && e.key === "ArrowLeft" && !isEditable && config.stepNumber > 1) {
          e.preventDefault();
          navigate(`/step${config.stepNumber - 1}`, { state: locationState });
          return;
        }

        // Alt+ArrowRight → next step (skip when typing)
        if (e.altKey && e.key === "ArrowRight" && !isEditable && config.stepNumber < 24) {
          e.preventDefault();
          navigate(`/step${config.stepNumber + 1}`, { state: locationState });
          return;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [formik, loading, navigate, locationState, showCoach, config.stepNumber]);

    // Step navigation
    const navigateToStep = (stepNum: number) => {
      navigate(`/step${stepNum}`, { state: locationState });
    };

    // Render a field based on its type
    const renderField = (field: FormFieldConfig) => {
      const value = fieldValues[field.name] || "";

      switch (field.type) {
        case "radio":
          return (
            <RadioGroup
              key={field.name}
              field={field}
              value={value}
              onChange={(v) => handleFieldChange(field.name, v)}
            />
          );
        case "textarea":
          return (
            <TextAreaField
              key={field.name}
              field={field}
              value={value}
              onChange={(v) => handleFieldChange(field.name, v)}
            />
          );
        case "text":
          return (
            <input
              key={field.name}
              type="text"
              className="form-control fs-7 fw-normal"
              id={field.name}
              name={field.name}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            />
          );
        case "select":
          return (
            <SelectField
              key={field.name}
              field={field}
              value={value}
              onChange={(v) => handleFieldChange(field.name, v)}
            />
          );
        default:
          return null;
      }
    };

    if (firestoreLoading && !formData) {
      return (
        <div className={`card ${className}`}>
          <div className="card-body d-flex justify-content-center align-items-center py-10">
            <span className="spinner-border spinner-border-lg text-primary" />
          </div>
        </div>
      );
    }

    const progressPercent = calculateProgress();

    return (
      <div className={`card ${className}`}>
        {/* Step Navigation Bar */}
        <div className="card-header border-0 pt-5 pb-3 px-7">
          {/* Back to Project link */}
          {locationState && (
            <button
              type="button"
              className="btn btn-sm btn-light w-100 text-start mb-3 d-flex align-items-center gap-2"
              onClick={() => navigate("/project", { state: locationState })}
              style={{ fontSize: "13px", color: "#475569", padding: "6px 10px" }}
            >
              <i className="bi bi-arrow-left" style={{ fontSize: "13px" }}></i>
              Back to {(locationState as Record<string, string>).projectTitle || "Project"}
            </button>
          )}

          <div className="d-flex align-items-center justify-content-between w-100">
            <button
              className="btn btn-sm btn-icon btn-light-primary"
              onClick={() => navigateToStep(config.stepNumber - 1)}
              disabled={config.stepNumber <= 1}
              title="Previous step"
            >
              <i className="bi bi-chevron-left fs-5"></i>
            </button>

            <div className="text-center flex-grow-1 mx-3">
              <span className="text-muted fw-semibold fs-8 d-block">
                Step {config.stepNumber} of 24
              </span>
              <span className="fw-bold text-gray-800 fs-6">{config.title}</span>
              {/* Category badge + Difficulty badge */}
              <div className="mt-1 d-flex align-items-center justify-content-center gap-1 flex-wrap">
                <span
                  style={{
                    display: "inline-block",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: category.color,
                    backgroundColor: category.lightColor,
                    padding: "2px 9px",
                    borderRadius: "9999px",
                    letterSpacing: "0.01em",
                  }}
                >
                  {category.label}
                </span>
                {(() => {
                  const diff = stepDifficultyMap[config.stepNumber];
                  const time = stepTimeMap[config.stepNumber];
                  if (!diff && !time) return null;
                  const { color: diffColor, label: diffLabel } = difficultyStyle[diff ?? "medium"];
                  return (
                    <span style={{ fontSize: "11px", color: "#94A3B8", fontWeight: 400, letterSpacing: "0.01em" }}>
                      {diff && <span style={{ color: diffColor, opacity: 0.8 }}>{diffLabel}</span>}
                      {diff && time && <span style={{ margin: "0 4px" }}>·</span>}
                      {time && <span>{time}</span>}
                    </span>
                  );
                })()}
              </div>

              {/* Step timestamp — last saved only */}
              {stepUpdatedAt && (
                <div className="mt-1" style={{ fontSize: "10px", color: "#CBD5E1", letterSpacing: "0.01em" }}>
                  Saved {timeAgo(stepUpdatedAt)}
                </div>
              )}
            </div>

            <button
              className="btn btn-sm btn-icon btn-light-primary"
              onClick={() => navigateToStep(config.stepNumber + 1)}
              disabled={config.stepNumber >= 24}
              title="Next step"
            >
              <i className="bi bi-chevron-right fs-5"></i>
            </button>
          </div>

          {/* Progress bar — coloured by category */}
          <div className="w-100 mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted fs-8">
                {filledCount} of {totalFields} fields completed
              </span>
              <span
                className="fw-bold fs-8"
                style={{ color: progressPercent >= 100 ? "#10B981" : progressPercent > 0 ? category.color : "#94A3B8" }}
              >
                {progressPercent}%
              </span>
            </div>
            <div
              className="rounded-pill overflow-hidden"
              style={{ height: "6px", background: "#F1F5F9" }}
            >
              <div
                className="h-100 rounded-pill"
                style={{
                  width: `${progressPercent}%`,
                  transition: "width 0.4s ease-out",
                  backgroundColor: progressPercent >= 100 ? "#10B981" : category.color,
                }}
              ></div>
            </div>
          </div>

          {/* Ask Coach button */}
          <div className="mt-3">
            {canUseFeature("ask_ai") ? (
              <button
                type="button"
                className="btn btn-sm btn-light-primary"
                onClick={() => setShowCoach(true)}
                style={{ fontSize: "13px", padding: "4px 12px" }}
              >
                <i className="bi bi-chat-dots me-1"></i>
                Ask Coach (AI)
              </button>
            ) : (
              <Link
                to="/profile/subscription"
                className="btn btn-sm btn-light py-1 px-3"
                style={{ fontSize: "13px" }}
              >
                <i className="bi bi-lock me-1 fs-8"></i>
                Ask Coach (Pro)
              </Link>
            )}
          </div>
        </div>

        <form
          id={`step${config.stepNumber}_form`}
          className="form d-flex flex-center card-body pt-0"
          onSubmit={formik.handleSubmit}
        >
          <div className={`card-body ${innerPadding}`}>
            {/* Description */}
            {config.description && (
              <p className="text-muted fs-7 mb-8">{config.description}</p>
            )}

            {/* Sections */}
            {config.sections.map((section, sectionIndex) => (
              <div key={`section_${sectionIndex}`} className="mb-10">
                <h5 className="fw-bold fs-5 text-dark mb-2">{section.title}</h5>
                {section.description && (
                  <p className="text-muted fs-7 mb-5">{section.description}</p>
                )}
                {section.fields.map((field) => renderField(field))}
              </div>
            ))}

            {/* Notes / Scratch Pad */}
            <div
              className="mb-6"
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {/* Collapsible toggle */}
              <button
                type="button"
                onClick={() => setNotesOpen((o) => !o)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "10px 14px",
                  background: notesOpen ? "#F8FAFC" : "#FFFFFF",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#475569",
                  transition: "background 150ms ease-out",
                }}
              >
                <span className="d-flex align-items-center gap-2">
                  <i className="bi bi-pencil-square" style={{ fontSize: "14px", color: category.color }}></i>
                  Notes
                  {notes.trim() && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: category.color,
                        background: category.lightColor,
                        padding: "1px 6px",
                        borderRadius: "9999px",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {notes.trim().split(/\s+/).length}w
                    </span>
                  )}
                </span>
                <i
                  className={`bi ${notesOpen ? "bi-chevron-up" : "bi-chevron-down"}`}
                  style={{ fontSize: "12px", transition: "transform 200ms ease-in-out" }}
                ></i>
              </button>

              {/* Notes body */}
              {notesOpen && (
                <div style={{ padding: "12px 14px", background: "#FFFFFF", borderTop: "1px solid #E2E8F0" }}>
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Jot down ideas, assumptions, or follow-up questions for this step…"
                    style={{
                      width: "100%",
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      color: "#0F172A",
                      resize: "vertical",
                      outline: "none",
                      fontFamily: "inherit",
                      lineHeight: 1.6,
                    }}
                  />
                  {/* Notes save status */}
                  <div className="mt-2" style={{ minHeight: "18px" }}>
                    {notesSaveStatus === "saving" && (
                      <span className="d-flex align-items-center gap-1" style={{ fontSize: "11px", color: "#94A3B8" }}>
                        <span className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px", borderWidth: "1.5px" }} />
                        Saving notes…
                      </span>
                    )}
                    {notesSaveStatus === "saved" && (
                      <span className="d-flex align-items-center gap-1" style={{ fontSize: "11px", color: "#10B981" }}>
                        <i className="bi bi-check-circle-fill"></i>
                        Notes saved
                      </span>
                    )}
                    {notesSaveStatus === "error" && (
                      <span className="d-flex align-items-center gap-1" style={{ fontSize: "11px", color: "#EF4444" }}>
                        <i className="bi bi-exclamation-circle-fill"></i>
                        Save failed
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Submit area with save status */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <button
                id={`step${config.stepNumber}_form_submit_button`}
                type="submit"
                className="btn btn-primary fw-bolder fs-6 px-8 py-4"
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {loading ? (
                  <span className="d-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" />
                    Saving...
                  </span>
                ) : (
                  "Save"
                )}
              </button>

              {/* Auto-save status indicator */}
              {saveStatus === "saving" && (
                <span className="d-flex align-items-center gap-2 text-muted fs-7">
                  <span className="spinner-border spinner-border-sm text-muted" />
                  Saving...
                </span>
              )}
              {saveStatus === "saved" && (
                <span className="d-flex align-items-center gap-2 text-success fs-7 fw-semibold">
                  <i className="bi bi-check-circle-fill text-success"></i>
                  Saved
                </span>
              )}
              {saveStatus === "error" && (
                <span className="d-flex align-items-center gap-2 text-danger fs-7 fw-semibold">
                  <i className="bi bi-exclamation-circle-fill text-danger"></i>
                  Save failed
                </span>
              )}
            </div>

            {/* Keyboard shortcut hint */}
            <div
              className="mb-6 text-center"
              style={{ fontSize: "11px", color: "#CBD5E1", letterSpacing: "0.01em", opacity: 0.8 }}
            >
              <kbd style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px", fontSize: "11px", fontFamily: "inherit" }}>
                {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}+S
              </kbd>{" "}
              save
              {config.stepNumber > 1 && (
                <>
                  {" · "}
                  <kbd style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px", fontSize: "11px", fontFamily: "inherit" }}>
                    Alt+←
                  </kbd>{" "}
                  prev step
                </>
              )}
              {config.stepNumber < 24 && (
                <>
                  {" · "}
                  <kbd style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px", fontSize: "11px", fontFamily: "inherit" }}>
                    Alt+→
                  </kbd>{" "}
                  next step
                </>
              )}
              {locationState && (
                <>
                  {" · "}
                  <kbd style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px", fontSize: "11px", fontFamily: "inherit" }}>
                    Esc
                  </kbd>{" "}
                  back to project
                </>
              )}
            </div>
          </div>
        </form>

        {/* Step Coach Modal */}
        {showCoach && (
          <StepCoachModal
            projectID={projectID}
            userID={userID}
            stepNumber={config.stepNumber}
            stepTitle={config.title}
            projectTitle={locationState?.projectTitle ?? ""}
            onClose={() => setShowCoach(false)}
          />
        )}
      </div>
    );
  }
);

GenericStepForm.displayName = "GenericStepForm";

export { GenericStepForm };
